import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { provisionStarterChecklists } from '@/lib/starterChecklists';

interface Props {
  tenantId: string | null;
}

/**
 * Shown when a company has no checklists yet. One tap loads the standard
 * daily food safety checks and drops them straight into the first one.
 */
export const StarterPackCard = ({ tenantId }: Props) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!tenantId) return;

      const { data: units } = await supabase
        .from('units')
        .select('id')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: true })
        .limit(1);

      const firstUnit = units?.[0]?.id ?? null;
      if (!firstUnit) {
        setShow(false);
        return;
      }

      const { count } = await supabase
        .from('checklists')
        .select('*', { count: 'exact', head: true })
        .eq('unit_id', firstUnit);

      setUnitId(firstUnit);
      setShow((count || 0) === 0);
    };

    check();
  }, [tenantId]);

  const load = async () => {
    if (!tenantId || !unitId) return;
    setWorking(true);
    try {
      const checklistId = await provisionStarterChecklists(tenantId, unitId, user?.id);
      toast.success('Your daily food safety checks are ready.');
      setShow(false);
      if (checklistId) navigate(`/checklists/${checklistId}/execute`);
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || 'Could not set up your checks. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  if (!show) return null;

  return (
    <Card className="glass-panel-elevated p-6 border-primary/50 mb-6">
      <div className="flex flex-col sm:flex-row items-start gap-4">
        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
          <Sparkles className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg mb-1">Get audit-ready in 30 seconds</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Load the standard daily checks every kitchen is expected to keep: fridge and freezer
            temperatures, opening checks and closing hygiene. You can edit them any time.
          </p>
          <Button onClick={load} disabled={working}>
            {working ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Setting up...
              </>
            ) : (
              'Set up my daily checks'
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default StarterPackCard;
